import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader, Edit, ArrowLeft, Trash2, Eye, FileText, Clock, CheckCircle2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { AuthContext } from '../../Auth/context/AuthContext';
import BidEditModal from '../components/BidEditModal';
// Hook: poll drawing-classification status
function useDrawingsStatus(bidId, token, intervalMs = 8000) {
  const [status, setStatus] = useState({ state: 'idle', processedPages: 0 });
  useEffect(() => {
    if (!bidId || !token) return;
    let timer = null;
    async function fetchStatus() {
      try {
        const resp = await axios.get(
          `/api/myteconstruction/bids/${bidId}/drawings/status`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setStatus(resp.data);
      } catch (err) {
        console.error('Drawings status poll failed', err);
      }
    }
    fetchStatus();
    timer = setInterval(fetchStatus, intervalMs);
    return () => clearInterval(timer);
  }, [bidId, token, intervalMs]);
  return status;
}


// Component to render drawing status UI
function DrawingStatus({ status, onViewDocuments }) {
  if (!status || status.state === 'idle') return null;
  return (
    <div className="mt-6 p-4 bg-gray-800 border border-gray-700 rounded text-gray-100">
      <h3 className="text-lg font-semibold mb-2">Drawing Analysis</h3>
      {status.state === 'queued' && (
        <div className="flex items-center space-x-2">
          <Clock className="h-5 w-5 text-yellow-300" />
          <span className="inline-flex items-center px-2 py-0.5 rounded bg-yellow-600/20 text-yellow-300 text-sm font-medium">Queued</span>
          <p className="text-sm text-gray-400">Your PDF binder is waiting to be processed…</p>
        </div>
      )}
      {status.state === 'working' && (
        <div className="flex items-center space-x-2">
          <Loader className="h-5 w-5 animate-spin text-green-300" />
          <span className="inline-flex items-center px-2 py-0.5 rounded bg-green-600/20 text-green-300 text-sm font-medium">{status.processedPages} pages processed</span>
          <button onClick={onViewDocuments} className="underline text-sm text-blue-300 hover:text-blue-200">View Documents</button>
        </div>
      )}
      {status.state === 'done' && (
        <div className="flex items-center space-x-2">
          <CheckCircle2 className="h-5 w-5 text-green-300" />
          <span className="inline-flex items-center px-2 py-0.5 rounded bg-green-600/20 text-green-300 text-sm font-medium">Completed</span>
          <button onClick={onViewDocuments} className="underline text-sm text-blue-300 hover:text-blue-200">Open Documents</button>
        </div>
      )}
    </div>
  );
}

// Maximum allowed attachment size (5 GB)
const MAX_SIZE = 5 * 1024 * 1024 * 1024;
const BidDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { auth } = useContext(AuthContext);
  const authToken = auth.token;

  const [bid, setBid] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const fileInputRef = useRef(null);
  // drawing-pipeline status via hook
  const drawingStatus = useDrawingsStatus(id, authToken);
     // Load bid details on mount
        useEffect(() => {
          async function loadBid() {
            setLoading(true);
            try {
              const resp = await axios.get(
                `/api/myteconstruction/bids/${id}`,
                { headers: { Authorization: `Bearer ${authToken}` } }
              );
              setBid(resp.data);
            } catch (err) {
              console.error(err);
              setError(err.response?.data?.error || 'Failed to load bid');
            } finally {
              setLoading(false);
            }
          }
          loadBid();
        }, [id, authToken]);

  const handleSave = (updated) => {
    setBid(updated);
    setShowEdit(false);
    toast.success('Bid updated successfully');
  };

  if (loading) {
    return (
      <div className="flex justify-center py-6">
        <Loader className="h-8 w-8 text-gray-400 animate-spin" />
      </div>
    );
  }
  if (error) {
    return <p className="text-red-500">{error}</p>;
  }
  if (!bid) return null;

  const colorFor = (s) => ({ open: 'bg-blue-600', won: 'bg-green-600', lost: 'bg-red-600', archived: 'bg-gray-500' }[s] || 'bg-gray-600');

  return (
    <div className="p-4 space-y-4">
      {/* Attachments section */}
      <div>
        <h2 className="text-xl font-semibold">Attachments</h2>
        <div className="mt-2 flex items-center space-x-2">
          <button
            type="button"
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            className="flex items-center bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
          >
            <Edit className="h-4 w-4 mr-1" />
            Upload Attachment
          </button>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={async (e) => {
              const file = e.target.files[0];
              if (!file) return;
              // size guard: limit to 5 GB
              if (file.size > MAX_SIZE) {
                toast.error('File is larger than 5 GB. Please choose a smaller file.');
                return;
              }
              try {
                // Get presigned URL
                const signResp = await axios.post(
                  `/api/myteconstruction/bids/${id}/attachments/sign`,
                  { filename: file.name, contentType: file.type, size: file.size },
                  { headers: { Authorization: `Bearer ${auth.token}` } }
                );
                // Upload to S3
                await axios.put(signResp.data.uploadUrl, file, {
                  headers: {
                    'Content-Type': file.type,
                    // no ACL header: bucket owner enforced policy
                  },
                  maxContentLength: Infinity,
                  maxBodyLength: Infinity,
                });
                // Save metadata
                const saveResp = await axios.post(
                  `/api/myteconstruction/bids/${id}/attachments`,
                  {
                    key: signResp.data.key,
                    filename: file.name,
                    size: file.size,
                    contentType: file.type,
                  },
                  { headers: { Authorization: `Bearer ${auth.token}` } }
                );
                // Append new attachment, building URL from presigned upload URL base
                const baseUrl = signResp.data.uploadUrl.split('?')[0];
                const newAtt = { ...saveResp.data, url: baseUrl };
                setBid((prev) => ({
                  ...prev,
                  attachments: [...(prev.attachments || []), newAtt],
                }));
                toast.success('Attachment uploaded');
              } catch (err) {
                console.error(err);
                toast.error('Failed to upload attachment');
              }
            }}
          />
        </div>
        <ul className="mt-2 space-y-1">
          {(bid.attachments || []).map((att) => {
            // Download via authenticated proxy (streams S3 through server)
            const { key, filename, size } = att;
            const kb = (size / 1024).toFixed(1);
            const keyB64 = btoa(key);
            const downloadUrl = `/api/myteconstruction/bids/${id}/attachments/download/${keyB64}`;
            return (
              <li key={key} className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const resp = await axios.get(downloadUrl, {
                        headers: { Authorization: `Bearer ${auth.token}` },
                        responseType: 'blob',
                      });
                      const blobUrl = URL.createObjectURL(resp.data);
                      window.open(blobUrl, '_blank');
                    } catch (e) {
                      toast.error('Unable to download file');
                    }
                  }}
                  className="flex items-center space-x-1 text-blue-600 hover:underline"
                >
                  <Eye className="h-5 w-5" />
                  <span>{filename}</span>
                </button>
                <span className="text-sm text-gray-500">{kb} KB</span>
              </li>
            );
          })}
        </ul>
        <DrawingStatus status={drawingStatus} onViewDocuments={() => navigate('documents')} />
        <div className="mt-4">
          <button
            type="button"
            onClick={() => navigate('documents')}
            className="flex items-center bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
          >
            <FileText className="h-4 w-4 mr-1" />
            View Documents
          </button>
        </div>
      </div>
      <button
        onClick={() => navigate(-1)}
        className="flex items-center text-gray-400 hover:underline"
      >
        <ArrowLeft className="h-5 w-5 mr-1" />
        Back to list
      </button>
      <h1 className="text-2xl font-bold">{bid.name}</h1>
      <div className="space-y-1">
        <p><strong>Client:</strong> {bid.client || '-'}</p>
        <p><strong>Due Date:</strong> {bid.dueDate ? new Date(bid.dueDate).toLocaleDateString() : '-'}</p>
        <p>
          <strong>Status:</strong>{' '}
          <span className={`px-2 py-1 rounded capitalize ${colorFor(bid.status)}`}>{bid.status}</span>
        </p>
      </div>
      <div className="flex space-x-4">
        <button
          onClick={() => setShowEdit(true)}
          className="flex items-center bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          <Edit className="h-4 w-4 mr-1" />
          Edit Bid
        </button>
        <button
          onClick={async () => {
            if (!window.confirm('Are you sure you want to delete this bid?')) return;
            try {
              await axios.delete(
                `/api/myteconstruction/bids/${id}`,
                { headers: { Authorization: `Bearer ${auth.token}` } }
              );
              toast.success('Bid deleted successfully');
              navigate(-1);
            } catch (err) {
              const msg = err.response?.data?.error || 'Failed to delete bid';
              toast.error(msg);
            }
          }}
          className="flex items-center bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
        >
          <Trash2 className="h-4 w-4 mr-1" />
          Delete Bid
        </button>
      </div>
      {showEdit && (
        <BidEditModal
          bid={bid}
          onClose={() => setShowEdit(false)}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

export default BidDetailPage;